// API client wrapper. All requests go cross-origin to api.inkling.family
// with `credentials: include` so the httpOnly session cookie travels along.
import type {
  CalendarEvent,
  Family,
  FamilyMember,
  Home,
  Subscription,
} from "@inkling/shared";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.inkling.family";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, body, `${res.status} ${res.statusText}: ${path}`);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  // auth
  requestMagicLink: (email: string, variant?: string) =>
    request<{ ok: true }>(`/api/v1/auth/request-magic-link`, {
      method: "POST",
      body: JSON.stringify(variant ? { email, variant } : { email }),
    }),
  verifyToken: (token: string) =>
    request<{ ok: true; email: string; variant: "A" | "B" | "C" | null }>(
      `/api/v1/auth/verify?token=${encodeURIComponent(token)}`,
      { method: "GET" },
    ),
  me: () => request<{ email: string }>(`/api/v1/auth/me`),
  myFamily: () =>
    request<{ family: Family | null; member: FamilyMember | null }>(`/api/v1/me/family`),
  logout: () => request<{ ok: true }>(`/api/v1/auth/logout`, { method: "POST" }),

  // families
  createFamily: (body: {
    name: string;
    homeLocation?: string | null;
    homeTimezone?: string;
    adminDisplayName: string;
    adminPhoneNumber: string;
    existingTwilioNumber?: string;
  }) =>
    request<{ family: Family; admin: FamilyMember | null }>(`/api/v1/families`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getFamily: (id: string) => request<{ family: Family }>(`/api/v1/families/${id}`),
  listMembers: (id: string) =>
    request<{ members: FamilyMember[] }>(`/api/v1/families/${id}/members`),
  listHomes: (familyId: string) =>
    request<{ homes: Home[] }>(`/api/v1/families/${familyId}/homes`),
  createHome: (
    familyId: string,
    body: {
      label: string;
      location: string | null;
      timezone: string;
      isDefault?: boolean;
    },
  ) =>
    request<{ home: Home }>(`/api/v1/families/${familyId}/homes`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateHome: (
    familyId: string,
    homeId: string,
    body: {
      label?: string;
      location?: string | null;
      timezone?: string;
      isDefault?: boolean;
    },
  ) =>
    request<{ home: Home }>(`/api/v1/families/${familyId}/homes/${homeId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteHome: (familyId: string, homeId: string) =>
    request<{ ok: true }>(`/api/v1/families/${familyId}/homes/${homeId}`, {
      method: "DELETE",
    }),
  inviteMember: (
    id: string,
    body: {
      phoneNumber: string;
      displayName: string;
      email?: string | null;
      inviteVia?: "sms" | "email";
    },
  ) =>
    request<{ member: FamilyMember; status: string }>(
      `/api/v1/families/${id}/members`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  resendInvite: (
    familyId: string,
    memberId: string,
    via?: "sms" | "email",
  ) =>
    request<{ status: string; inviteSid?: string }>(
      `/api/v1/families/${familyId}/members/${memberId}/resend-invite${
        via ? `?via=${via}` : ""
      }`,
      { method: "POST" },
    ),
  removeMember: (familyId: string, memberId: string) =>
    request<{ status: string; memberId: string }>(
      `/api/v1/families/${familyId}/members/${memberId}`,
      { method: "DELETE" },
    ),

  // Family join requests
  submitJoinRequest: (body: {
    adminEmail?: string | null;
    adminPhone?: string | null;
    requesterName: string;
    requesterEmail?: string | null;
    requesterPhone?: string | null;
    message?: string | null;
  }) =>
    request<{
      request: { id: string; status: string };
      status: string;
      deliveredVia?: "email" | "sms";
    }>(`/api/v1/join-requests`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listJoinRequests: (familyId: string) =>
    request<{
      requests: {
        id: string;
        familyId: string;
        requesterEmail: string | null;
        requesterPhone: string | null;
        requesterName: string;
        message: string | null;
        adminNotifyMethod: "email" | "sms";
        status: "pending" | "approved" | "denied";
        createdAt: string;
      }[];
    }>(`/api/v1/families/${familyId}/join-requests`),
  approveJoinRequest: (familyId: string, reqId: string) =>
    request<{ status: string; member: FamilyMember }>(
      `/api/v1/families/${familyId}/join-requests/${reqId}/approve`,
      { method: "POST" },
    ),
  denyJoinRequest: (familyId: string, reqId: string) =>
    request<{ status: string }>(
      `/api/v1/families/${familyId}/join-requests/${reqId}/deny`,
      { method: "POST" },
    ),

  // Google OAuth
  googleStatus: (as: "family" | "member", memberId?: string) =>
    request<
      | {
          connected: true;
          providerAccountId: string | null;
          expiresAt: string | null;
          scope: string;
        }
      | { connected: false; reason?: string }
    >(
      `/api/v1/google/oauth/status?as=${as}${memberId ? `&memberId=${memberId}` : ""}`,
    ),
  googleStartUrl: (as: "family" | "member", memberId?: string) =>
    `/api/v1/google/oauth/start?as=${as}${memberId ? `&memberId=${memberId}` : ""}`,

  // events
  listEvents: (familyId: string, from: Date, to: Date) =>
    request<{ events: CalendarEvent[] }>(
      `/api/v1/events/families/${familyId}?from=${from.toISOString()}&to=${to.toISOString()}`,
    ),
  patchEvent: (
    eventId: string,
    body: {
      title?: string;
      description?: string | null;
      startTime?: string;
      endTime?: string | null;
      location?: string | null;
      recurrenceRrule?: string | null;
    },
  ) =>
    request<{ event: CalendarEvent }>(`/api/v1/events/${eventId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteEvent: (eventId: string) =>
    request<{ ok: true }>(`/api/v1/events/${eventId}`, { method: "DELETE" }),
  eventSourceMediaUrl: (eventId: string, idx: number) =>
    request<{ url: string; expiresIn: number }>(
      `/api/v1/events/${eventId}/source-media/${idx}`,
    ),
  submitFeedback: (body: {
    pageUrl: string;
    fromEmail?: string | null;
    body?: string | null;
    interactionPreference?: string | null;
    frictionPoint?: string | null;
    landingVariant?: "A" | "B" | "C" | null;
  }) =>
    request<{ ok: true }>(`/api/v1/feedback`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // billing
  usage: (familyId: string) =>
    request<{
      tier: "free" | "paid";
      status: string;
      eventsThisMonth: number;
      limit: number | null;
      overage: number;
      currentPeriodEnd: string | null;
    }>(`/api/v1/billing/families/${familyId}/usage`),
  subscribe: (familyId: string) =>
    request<{ url: string }>(`/api/v1/billing/families/${familyId}/subscribe`, {
      method: "POST",
    }),
  portal: (familyId: string) =>
    request<{ url: string }>(`/api/v1/billing/families/${familyId}/portal`, {
      method: "POST",
    }),
};

export type { CalendarEvent, Family, FamilyMember, Home, Subscription };
